import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation, useMatch, useBlocker } from "react-router-dom";
import { Button, Grid, Group, Title, Drawer, Collapse, Modal, Stack, Text, Space, useMantineTheme } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";

import {
	useGetWorkOrderSchedulesQuery,
	useGetWorkOrderScheduleByIdQuery,
	useCreateWorkOrderScheduleMutation,
	useUpdateWorkOrderScheduleMutation,
	useDeleteWorkOrderScheduleMutation,
	useActivateWorkOrderScheduleMutation,
	useDeactivateWorkOrderScheduleMutation
} from "../../api/workorderschedule.api";
import { CreateWorkOrderScheduleDto, UpdateWorkOrderScheduleDto } from "../../models/workorderschedule.models";
import { ScheduleList } from "../../Components/WorkOrderSchedules/ScheduleList";
import { ScheduleEditView } from "../../Components/WorkOrderSchedules/ScheduleEditView";
import { ScheduleDetailView } from "../../Components/WorkOrderSchedules/ScheduleDetailView";
import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../../Components/RoleTemplate";
import { useSearch } from "../../functions/SearchBox.hook";

const WorkOrderSchedulesPage = () => {
	const navigate = useNavigate();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
	const params = useParams<{ scheduleId?: string }>();
	const isEditing = useMatch("/workorder-schedules/:scheduleId/edit") !== null;
	const isNew = params.scheduleId === "new";
	const scheduleIdFromParams = params.scheduleId || null;
	const { SearchBox, visible: searchVisible } = useSearch();

	const { data: schedules, isPending: isLoadingSchedules } = useGetWorkOrderSchedulesQuery();
	const [selectedSchedule, setSelectedSchedule] = useState<any>(null);
	const { data: schedule, isLoading } = useGetWorkOrderScheduleByIdQuery(selectedSchedule?.ScheduleId || scheduleIdFromParams || null);
	// const { data: previewExecutions } = useGetWorkOrderSchedulePreviewQuery(selectedSchedule?.ScheduleId || scheduleIdFromParams || null, 10);

	const [discardChangesModalOpened, { open: openDiscardChangesModal, close: closeDiscardChangesModal }] = useDisclosure(false);
	const [deleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
	const [detailViewDrawerOpened, { open: openDetailViewDrawer, close: closeDetailViewDrawer }] = useDisclosure(false);
	const [isChanged, setIsChanged] = useState(false);
	const blockerHandledRef = useRef(false);

	const { mutate: createSchedule, isPending: isCreating } = useCreateWorkOrderScheduleMutation();
	const { mutate: updateSchedule, isPending: isUpdating } = useUpdateWorkOrderScheduleMutation();
	const { mutate: deleteSchedule } = useDeleteWorkOrderScheduleMutation();
	const { mutate: activateSchedule } = useActivateWorkOrderScheduleMutation();
	const { mutate: deactivateSchedule } = useDeactivateWorkOrderScheduleMutation();

	const isSaving = isCreating || isUpdating;

	// Block navigation when there are unsaved changes
	const blocker = useBlocker(
		useCallback(
			({ currentLocation, nextLocation }: { currentLocation: any; nextLocation: any }) =>
				isChanged && !isSaving && currentLocation.pathname !== nextLocation.pathname,
			[isChanged, isSaving]
		)
	);

	// Open modal when navigation is blocked
	useEffect(() => {
		if (blocker.state === "blocked") {
			if (!blockerHandledRef.current) {
				blockerHandledRef.current = true;
				openDiscardChangesModal();
			}
		} else if (blocker.state === "unblocked" || blocker.state === "proceeding") {
			blockerHandledRef.current = false;
		}
	}, [blocker.state, openDiscardChangesModal]);

	useEffect(() => {
		if (scheduleIdFromParams) {
			const sched = schedules?.find((s: any) => s.ScheduleId === scheduleIdFromParams);
			if (sched) {
				setSelectedSchedule(sched);
			}
		} else if (schedules && schedules.length > 0) {
			setSelectedSchedule(schedules[0]);
		}
	}, [scheduleIdFromParams, schedules]);

	useEffect(() => {
		if (!scheduleIdFromParams && isMobile) {
			closeDetailViewDrawer();
		}
	}, [scheduleIdFromParams, isMobile]);

	const handleEdit = () => {
		navigate(`/workorder-schedules/${selectedSchedule.ScheduleId}/edit`);
		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleNew = () => {
		setIsChanged(false);
		setSelectedSchedule(null);
		navigate(`/workorder-schedules/new`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleScheduleSelect = (selectedSched: any | null) => {
		if (!selectedSched) return;

		setSelectedSchedule(selectedSched);
		navigate(`/workorder-schedules/${selectedSched.ScheduleId}`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleSave = (values: CreateWorkOrderScheduleDto | UpdateWorkOrderScheduleDto) => {
		if (isNew) {
			createSchedule(values as CreateWorkOrderScheduleDto, {
				onSuccess: (response: any) => {
					notifications.show({
						title: "Schedule created",
						message: "Work order schedule has been created successfully",
						color: "green",
					});
					setIsChanged(false);
					navigate(`/workorder-schedules/${response.data.ScheduleId}`, { replace: true });
				},
				onError: (error) => {
					notifications.show({
						title: "Error",
						message: "Failed to create schedule",
						color: "red",
					});
				},
			});
		} else {
			updateSchedule({ id: selectedSchedule.ScheduleId, model: values as UpdateWorkOrderScheduleDto }, {
				onSuccess: (response) => {
					notifications.show({
						title: "Schedule updated",
						message: "Work order schedule has been updated successfully",
						color: "green",
					});
					setIsChanged(false);
					navigate(`/workorder-schedules/${selectedSchedule.ScheduleId}`);
				},
				onError: (error) => {
					notifications.show({
						title: "Error",
						message: "Failed to update schedule",
						color: "red",
					});
				},
			});
		}
	};

	const handleDelete = () => {
		if (!schedule) return;

		closeDeleteModal();
		deleteSchedule(schedule.ScheduleId, {
			onSuccess: () => {
				notifications.show({
					title: "Schedule deleted",
					message: "Work order schedule has been deleted successfully",
					color: "green",
				});
				setSelectedSchedule(null);
				navigate(`/workorder-schedules`, { replace: true });
			},
			onError: (error) => {
				notifications.show({
					title: "Error",
					message: "Failed to delete schedule",
					color: "red",
				});
			},
		});
	};

	const handleActivate = () => {
		if (!schedule) return;

		activateSchedule(schedule.ScheduleId, {
			onSuccess: () => {
				notifications.show({
					title: "Schedule activated",
					message: "Work order schedule has been activated successfully",
					color: "green",
				});
			},
			onError: (error) => {
				notifications.show({
					title: "Error",
					message: "Failed to activate schedule",
					color: "red",
				});
			},
		});
	};

	const handleDeactivate = () => {
		if (!schedule) return;

		deactivateSchedule(schedule.ScheduleId, {
			onSuccess: () => {
				notifications.show({
					title: "Schedule deactivated",
					message: "Work order schedule has been deactivated successfully",
					color: "green",
				});
			},
			onError: (error) => {
				notifications.show({
					title: "Error",
					message: "Failed to deactivate schedule",
					color: "red",
				});
			},
		});
	};

	const handleClose = () => {
		navigate(-1);

		if (isMobile && isNew) {
			closeDetailViewDrawer();
		}
	};

	const handleChanged = (changed: boolean) => {
		setIsChanged(changed);
	};

	const handleDiscardChanges = () => {
		closeDiscardChangesModal();
		setIsChanged(false);
		
		if (blocker.state === "blocked") {
			blocker.proceed();
		}

		if (isMobile && isNew) {
			closeDetailViewDrawer();
		}
	};

	const handleCancelDiscard = () => {
		closeDiscardChangesModal();
		
		if (blocker.state === "blocked") {
			blocker.reset();
		}
	};

	const handleDetailViewDrawerClose = () => {
		navigate(-1);
	};

	// const scheduleWithPreview = schedule && previewExecutions
	// 	? { ...schedule, UpcomingExecutions: previewExecutions }
	// 	: schedule;

	return <>
		<DeleteScheduleModal
			schedule={schedule}
			open={deleteModalOpen}
			onDelete={handleDelete}
			onClose={closeDeleteModal}
		/>

		<DiscardChangesModal
			open={discardChangesModalOpened}
			onDiscard={handleDiscardChanges}
			onClose={handleCancelDiscard}
		/>

		<Drawer opened={detailViewDrawerOpened} onClose={handleDetailViewDrawerClose} withCloseButton={false} padding={0} size="xl" position="right">
			{isEditing || isNew ? (
				<ScheduleEditView
					schedule={!isNew ? schedule : null}
					isLoading={isLoading}
					onSubmit={handleSave}
					onCancel={handleClose}
					isSaving={isSaving}
					onChanged={handleChanged}
				/>
			) : (
				<ScheduleDetailView
					schedule={schedule}
					isLoading={isLoading}
					onEdit={handleEdit}
					onDelete={openDeleteModal}
					onActivate={handleActivate}
					onDeactivate={handleDeactivate}
					onClose={handleDetailViewDrawerClose}
				/>
			)}
		</Drawer>

		<Collapse in={searchVisible} mb="md" hiddenFrom="sm">
			{SearchBox}
		</Collapse>

		<Group justify="space-between">
			<Title order={2}>Work Order Schedules</Title>
			<RoleTemplate role={AzureADRoles.Admin}>
				<Button onClick={handleNew} radius="xl" leftSection={<IconPlus />}>
					New Schedule
				</Button>
			</RoleTemplate>
		</Group>

		<Grid mt="lg" gutter="md">
			<Grid.Col span={{ base: 12, xs: ((!schedules || schedules.length === 0) && !isNew && !isEditing) ? 12 : 5 }}>
				<ScheduleList
					schedules={schedules}
					isLoading={isLoadingSchedules}
					selectedScheduleId={selectedSchedule?.ScheduleId}
					onSelectSchedule={handleScheduleSelect}
				/>
			</Grid.Col>
			<Grid.Col span={{ base: 12, xs: 7, md: 7, lg: 7 }} visibleFrom="xs">
				{isEditing || isNew ? (
					<ScheduleEditView
						schedule={!isNew ? schedule : null}
						isLoading={isLoading}
						onSubmit={handleSave}
						onCancel={handleClose}
						isSaving={isSaving}
						onChanged={handleChanged}
					/>
				) : schedules && schedules.length > 0 ? (
					<ScheduleDetailView
						schedule={schedule}
						isLoading={isLoading}
						onEdit={handleEdit}
						onDelete={openDeleteModal}
						onActivate={handleActivate}
						onDeactivate={handleDeactivate}
						onClose={handleDetailViewDrawerClose}
					/>
				) : <></>}
			</Grid.Col>
		</Grid>
	</>
};

export default WorkOrderSchedulesPage;

const DiscardChangesModal = ({ open, onDiscard, onClose }: { open: boolean, onClose: () => void, onDiscard: () => void }) => (
	<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Discard unsaved changes?</Text>} radius="lg" zIndex={1000}>
		<Stack>
			<Text>If you leave now, you'll lose unsaved changes to this schedule.</Text>
			<Group justify="right">
				<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
				<Button variant="filled" onClick={onDiscard} radius="lg">Discard Changes</Button>
			</Group>
		</Stack>
	</Modal>
);

const DeleteScheduleModal = ({ schedule, open, onDelete, onClose }: { schedule?: any, open: boolean, onDelete: () => void, onClose: () => void }) => {
	return (
		<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Delete Schedule</Text>} radius="lg" zIndex={1000}>
			<Stack>
				<Text>Are you sure you want to delete this schedule?</Text>
				<Space h="xs" />
				<Group justify="right">
					<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
					<Button color="red" onClick={onDelete} radius="lg">Delete</Button>
				</Group>
			</Stack>
		</Modal>
	);
};
